import { useEffect } from 'react';

export interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  metaKey?: boolean;
  callback: () => void;
  description: string;
}

export const useKeyboardShortcuts = (shortcuts: KeyboardShortcut[]) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        return;
      }

      for (const shortcut of shortcuts) {
        const keyMatch = event.key.toLowerCase() === shortcut.key.toLowerCase();
        const ctrlMatch = shortcut.ctrlKey === undefined || event.ctrlKey === shortcut.ctrlKey;
        const shiftMatch = shortcut.shiftKey === undefined || event.shiftKey === shortcut.shiftKey;
        const altMatch = shortcut.altKey === undefined || event.altKey === shortcut.altKey;
        const metaMatch = shortcut.metaKey === undefined || event.metaKey === shortcut.metaKey;

        if (keyMatch && ctrlMatch && shiftMatch && altMatch && metaMatch) {
          event.preventDefault();
          shortcut.callback();
          break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts]);
};

export const defaultShortcuts = (onViewChange: (view: string) => void, currentRole: string) => {
  const isContractor = currentRole === 'contractor';

  const shortcuts: KeyboardShortcut[] = [
    {
      key: 'd',
      callback: () => onViewChange('dashboard'),
      description: 'Go to Dashboard',
    },
    {
      key: 'm',
      callback: () => onViewChange('messages'),
      description: 'Open Messages',
    },
    {
      key: 's',
      callback: () => onViewChange('settings'),
      description: 'Open Settings',
    },
    {
      key: '?',
      shiftKey: true,
      callback: () => {
        const event = new CustomEvent('show-shortcuts');
        window.dispatchEvent(event);
      },
      description: 'Show keyboard shortcuts',
    },
  ];

  if (isContractor) {
    shortcuts.push(
      {
        key: 'p',
        callback: () => onViewChange('profile'),
        description: 'View Profile',
      },
      {
        key: 'j',
        callback: () => onViewChange('jobs'),
        description: 'Find Jobs',
      },
      {
        key: 'i',
        callback: () => onViewChange('insurance'),
        description: 'View Insurance',
      }
    );
  } else {
    shortcuts.push(
      {
        key: 'j',
        callback: () => onViewChange('manage-jobs'),
        description: 'Manage Jobs',
      },
      {
        key: 't',
        callback: () => onViewChange('trades'),
        description: 'Find Trades',
      },
      {
        key: 'c',
        callback: () => onViewChange('compliance'),
        description: 'View Compliance',
      }
    );
  }

  return shortcuts;
};
